/**
 * Derive primary UI shell role from a held functional role set,
 * and collapse display labels (supervisor subsumes provider).
 */

import type { UnifyRole } from './resources/practitioner.js';

const PRIMARY_PRIORITY: readonly UnifyRole[] = [
  'unify-admin',
  'practice-owner',
  'supervisor',
  'provider',
  'practice-admin',
] as const;

const ROLE_DISPLAY_LABELS: Record<UnifyRole, string> = {
  'unify-admin': 'Unify Admin',
  'practice-owner': 'Practice Owner',
  'practice-admin': 'Practice Admin',
  supervisor: 'Supervisor',
  provider: 'Provider',
};

/**
 * Pick the single nav-shell / ui-permissions primary from held codes.
 * Supervisor wins over provider when both are present.
 * Returns undefined when the set is empty (offboard path).
 */
export function derivePrimaryRole(
  held: readonly string[],
): UnifyRole | undefined {
  const set = new Set(held.filter(Boolean));
  for (const role of PRIMARY_PRIORITY) {
    if (set.has(role)) return role;
  }
  return undefined;
}

/**
 * Roles to show as chips / badges.
 * When both provider and supervisor are held, only Supervisor is shown
 * (provider remains in data; optional "Also treats clients" is separate).
 */
export function collapseRolesForDisplay(
  held: readonly string[],
): UnifyRole[] {
  const set = new Set(held.filter(Boolean) as UnifyRole[]);
  const out: UnifyRole[] = [];
  const hasSupervisor = set.has('supervisor');
  for (const role of PRIMARY_PRIORITY) {
    if (!set.has(role)) continue;
    if (role === 'provider' && hasSupervisor) continue;
    out.push(role);
  }
  // Preserve any unknown order-stable extras not in priority list
  for (const role of set) {
    if (!(PRIMARY_PRIORITY as readonly string[]).includes(role) && !out.includes(role)) {
      out.push(role);
    }
  }
  return out;
}

export function roleDisplayLabel(role: string): string {
  return ROLE_DISPLAY_LABELS[role as UnifyRole] ?? role;
}

/** Comma-joined collapsed labels for headers / directory. */
export function formatCollapsedRoleLabels(held: readonly string[]): string {
  return collapseRolesForDisplay(held).map(roleDisplayLabel).join(', ');
}

/** True when held set includes provider treating capability (including via supervisor stack). */
export function holdsTreatingCapability(held: readonly string[]): boolean {
  return held.includes('provider') || held.includes('supervisor');
}

export function holdsSupervisingCapability(held: readonly string[]): boolean {
  return held.includes('supervisor');
}

/**
 * Normalize a role-set mutation: gaining supervisor keeps provider code.
 */
export function normalizeHeldRoles(held: readonly string[]): UnifyRole[] {
  const set = new Set<UnifyRole>();
  for (const r of held) {
    if ((PRIMARY_PRIORITY as readonly string[]).includes(r)) {
      set.add(r as UnifyRole);
    }
  }
  if (set.has('supervisor')) {
    set.add('provider');
  }
  return PRIMARY_PRIORITY.filter((r) => set.has(r));
}

/** True when two held sets normalize to the same ordered role list. */
export function sameRoleSet(a: readonly string[], b: readonly string[]): boolean {
  const na = normalizeHeldRoles(a);
  const nb = normalizeHeldRoles(b);
  return na.length === nb.length && na.every((role, i) => role === nb[i]);
}

/**
 * Remove a displayed role from the held set.
 * Losing supervisor keeps provider (demotion). Removing provider drops treating only.
 */
export function removeDisplayRoleFromHeld(
  heldNow: readonly string[],
  roleToRemove: string,
): UnifyRole[] {
  let next = heldNow.filter((r) => r !== roleToRemove);
  if (roleToRemove === 'provider') {
    next = next.filter((r) => r !== 'provider');
  }
  return normalizeHeldRoles(next);
}

/** Roles that can be added to `held` (not already present; clinical collapse aware). */
export function rolesAvailableToAdd(
  held: readonly string[],
  assignableByEditor: readonly string[],
): UnifyRole[] {
  const heldSet = new Set(held);
  const hasSupervisor = heldSet.has('supervisor');
  return assignableByEditor.filter((r): r is UnifyRole => {
    if (!(PRIMARY_PRIORITY as readonly string[]).includes(r)) return false;
    if (heldSet.has(r)) return false;
    // Provider is implied when supervisor is held — don't offer as separate add
    if (r === 'provider' && hasSupervisor) return false;
    return true;
  });
}
